import { useState } from "react";

import { Button, Group, NumberInput, NumberInputProps, Stack, Text } from "@mantine/core";
import { useFocusWithin } from "@mantine/hooks";

import { CapRaisedSlider } from "components/ExplorerHeader/CapRaisedDropdown";
import { Eyebrow } from "components/mantine-extended";
import { SectionToggle } from "components/SectionToggle";
import { useSearchRouteInfo } from "hooks/routing";

interface SidebarCapRaisedSectionProps {
  handleChange: (key: string, value: [number, number] | null) => void;
  queryParamName: string;
  noToggle?: boolean;
}

const MIN_VALUE = 0;
const MAX_VALUE = 999_000_000;
const STEP = 1_000_000;

export function SidebarCapRaisedSection(props: SidebarCapRaisedSectionProps): JSX.Element {
  const { query } = useSearchRouteInfo();
  const { ref, focused } = useFocusWithin();

  function getRangesFromQuery(): [number, number] {
    const minValue = query.capitalRaised?.[0] ?? MIN_VALUE;
    const maxValueFromQuery = query.capitalRaised?.[1];
    const maxValue = maxValueFromQuery === Infinity ? MAX_VALUE : maxValueFromQuery ?? MAX_VALUE;

    return [minValue, maxValue];
  }

  const [range, setRange] = useState<[number, number]>(getRangesFromQuery());

  function renderInteractiveSection() {
    const sharedInputProps: NumberInputProps = {
      min: MIN_VALUE,
      max: MAX_VALUE / STEP,
      hideControls: true,
      rightSection: <Text>M</Text>,
      onBlur: () => props.handleChange(props.queryParamName, stripMax(range)),
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          props.handleChange(props.queryParamName, stripMax(range));
        }
      },
    };

    return (
      <Stack gap={24}>
        <CapRaisedSlider
          handleChangeEnd={props.handleChange}
          queryParamName={props.queryParamName}
          range={range}
          onChange={(v) => {
            setRange(v);
          }}
        />
        <Stack>
          <Group
            justify="center"
            wrap="nowrap"
            sx={{
              fontWeight: 500,
            }}
          >
            <NumberInput
              value={range[0] / STEP}
              prefix="$"
              onChange={(v) => {
                if (typeof v === "number") {
                  const newVal = Math.min(v * STEP, MAX_VALUE);
                  setRange([newVal, range[1]]);
                }
              }}
              {...sharedInputProps}
            />

            <Text>-</Text>
            <div ref={ref}>
              <NumberInput
                value={range[1] / STEP}
                prefix="$"
                suffix={range[1] === MAX_VALUE ? "+" : ""}
                onChange={(v) => {
                  if (typeof v === "number") {
                    const newVal = Math.min(v * STEP, MAX_VALUE);
                    setRange([range[0], newVal]);
                  }
                }}
                {...sharedInputProps}
              />
            </div>
          </Group>
          <Group justify="space-between" px={6}>
            <Eyebrow>MIN</Eyebrow>
            <Eyebrow>MAX</Eyebrow>
          </Group>
        </Stack>
        <Group justify="flex-start">
          <Button
            variant="secondary"
            h={30}
            onClick={() => {
              setRange([MIN_VALUE, MAX_VALUE]);
              props.handleChange(props.queryParamName, null);
            }}
          >
            Clear
          </Button>
        </Group>
      </Stack>
    );
  }

  if (props.noToggle) {
    return renderInteractiveSection();
  }

  return (
    <SectionToggle title="Capital Raised">
      <Stack py="12px" gap={8}>
        {renderInteractiveSection()}
      </Stack>
    </SectionToggle>
  );
}

function formatNumber(value: number): string {
  const formatted = `$${(value / STEP).toLocaleString("en-US")}M`;
  if (value === MAX_VALUE) {
    return `${formatted}+`;
  } else {
    return formatted;
  }
}

function stripMax(value: [number, number]): [number, number] {
  if (value[1] === MAX_VALUE) {
    return [value[0], Infinity];
  }
  return value;
}

function isNoFilter(value: [number, number] | null): boolean {
  if (value === null) {
    return true;
  }
  return value[0] === MIN_VALUE && value[1] === MAX_VALUE;
}
